import React from "react";
import { getSelfServiceBaseUrl } from "../util/UrlUtil.mjs";
import { FetchStatus } from "../util/FetchStatus.mjs";
import { ContactInfoStatusValue } from "../interfaces/ContactInfoInterfaces.mjs";
import { getAllContactInfoStatuses } from "../util/ContactInfoUtil.mjs";
import ErrorUtil from "../util/ErrorUtil.mjs";
import { useInvoices } from "./InvoiceProvider.mjs";
const DEFAULT_USER_INFO_STATE = {
  userInfo: { displayName: "", email: "", phone: "", customerType: "", communicationLanguageCode: "" },
  status: FetchStatus.UNINITIALIZED
};
const DEFAULT_CONTACT_ADDRESS_STATE = {
  contactAddress: {},
  status: FetchStatus.UNINITIALIZED
};
const DEFAULT_CONTACT_INFO_STATUSES_STATE = {
  contactInfoStatuses: { email: [], phone: [], address: [], general: "" },
  status: FetchStatus.UNINITIALIZED
};
const ContactInfoProvider = (props) => {
  const { lang, tier, commonApi } = props;
  const { invoiceSettingsState, fetchInvoiceSettings } = useInvoices();
  const [userInfoState, setUserInfoState] = React.useState(DEFAULT_USER_INFO_STATE);
  const [contactAddressState, setContactAddressState] = React.useState(DEFAULT_CONTACT_ADDRESS_STATE);
  const [contactInfoStatusesState, setContactInfoStatusesState] = React.useState(DEFAULT_CONTACT_INFO_STATUSES_STATE);
  React.useEffect(() => {
    if (contactInfoStatusesState.status === FetchStatus.FETCHED) {
      const { general } = contactInfoStatusesState.contactInfoStatuses;
      if (getAllContactInfoStatuses(contactInfoStatusesState.contactInfoStatuses).some((status) => status !== ContactInfoStatusValue.OK) || general !== ContactInfoStatusValue.OK) {
        fetchUserInfo();
        fetchContactAddress();
        fetchInvoiceSettings();
      } else {
        updateCheckContactInfosFlag();
      }
    }
  }, [contactInfoStatusesState]);
  const isLoading = () => {
    const loadingFetchStatuses = [FetchStatus.UNINITIALIZED, FetchStatus.FETCHING];
    return loadingFetchStatuses.includes(contactInfoStatusesState.status) || loadingFetchStatuses.includes(userInfoState.status) || loadingFetchStatuses.includes(contactAddressState.status) || loadingFetchStatuses.includes(invoiceSettingsState.status);
  };
  const fetchCheckContactInfosFlag = async () => {
    try {
      const flag = (await commonApi.get("/user/contact-info-check", { headers: { "X-Origin-Referer": document.location.href } })).data;
      if (flag === "should_check") {
        fetchContactInfoStatuses();
      }
    } catch (_e) {
    }
  };
  const updateCheckContactInfosFlag = async () => {
    try {
      commonApi.post("/user/contact-info-validate-check");
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
    }
  };
  const goToSelfServiceInfoUpdate = () => {
    updateCheckContactInfosFlag();
    window.location.assign("".concat(getSelfServiceBaseUrl(tier)).concat(lang === "et" ? "" : "/".concat(lang), "/konto/andmete-uuendamine?goToUrl=").concat(window.location.href));
  };
  const fetchContactInfoStatuses = async () => {
    setContactInfoStatusesState({ ...DEFAULT_CONTACT_INFO_STATUSES_STATE, status: FetchStatus.FETCHING });
    try {
      const { data } = await commonApi.get("/user/contact-info-status");
      setContactInfoStatusesState({ contactInfoStatuses: data, status: FetchStatus.FETCHED });
    } catch (e) {
      setContactInfoStatusesState({ ...DEFAULT_CONTACT_INFO_STATUSES_STATE, status: FetchStatus.ERROR });
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
    }
  };
  const fetchUserInfo = async () => {
    setUserInfoState({ ...DEFAULT_USER_INFO_STATE, status: FetchStatus.FETCHING });
    try {
      const { data } = await commonApi.get("/user/info");
      setUserInfoState({ userInfo: data, status: FetchStatus.FETCHED });
    } catch (e) {
      setUserInfoState({ ...DEFAULT_USER_INFO_STATE, status: FetchStatus.ERROR });
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
    }
  };
  const fetchContactAddress = async () => {
    setContactAddressState({ ...DEFAULT_CONTACT_ADDRESS_STATE, status: FetchStatus.FETCHING });
    try {
      const { data } = await commonApi.get("/user/contact-address");
      setContactAddressState({ contactAddress: data, status: FetchStatus.FETCHED });
    } catch (e) {
      setContactAddressState({ ...DEFAULT_CONTACT_ADDRESS_STATE, status: FetchStatus.ERROR });
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
    }
  };
  const confirmContactInfos = async () => {
    try {
      await commonApi.put("/user/contact-info-confirm");
      return "confirm_ok";
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      return "confirm_failed";
    }
  };
  return /* @__PURE__ */ React.createElement(
    ContactInfoContext.Provider,
    {
      value: {
        userInfoState,
        contactAddressState,
        contactInfoStatusesState,
        isLoading,
        fetchCheckContactInfosFlag,
        updateCheckContactInfosFlag,
        goToSelfServiceInfoUpdate,
        confirmContactInfos
      }
    },
    props.children
  );
};
const ContactInfoContext = React.createContext({
  userInfoState: DEFAULT_USER_INFO_STATE,
  contactAddressState: DEFAULT_CONTACT_ADDRESS_STATE,
  contactInfoStatusesState: DEFAULT_CONTACT_INFO_STATUSES_STATE,
  isLoading: () => false,
  fetchCheckContactInfosFlag: () => void 0,
  updateCheckContactInfosFlag: () => void 0,
  goToSelfServiceInfoUpdate: () => void 0,
  confirmContactInfos: () => void 0
});
const useContactInfo = () => {
  return React.useContext(ContactInfoContext);
};
export {
  ContactInfoContext,
  ContactInfoProvider,
  useContactInfo
};
